import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import type {RentalCalendarRow} from '../types/RentalCalendarRow';
import type {RentalCalendarRowGroup} from '../types/RentalCalendarRowGroup';

/**
 * Splits the calendar rows into the vehicle-category groups the grid draws a separator row in
 * front of. Rows keep the order they arrive in, so the title sort applied when they were built
 * survives inside each group. Vehicles without a category collect in one trailing group rather
 * than disappearing, and the groups themselves are ordered by label with that one always last.
 */
export function groupCalendarRows(
  rows: RentalCalendarRow[],
  categoryLabels: Map<string, string>
): RentalCalendarRowGroup[] {
  // The category is optional on the rental vehicle, so a fleet that sets none anywhere would
  // otherwise get a single "not set" heading restating that over every row
  if (rows.every((row) => isNil(row.vehicle.vehicleCategoryId))) {
    return [{key: WITHOUT_VALUE_KEY, label: null, rows}];
  }

  const rowsByKey = rows.reduce<Map<string, RentalCalendarRow[]>>((groups, row) => {
    const key = row.vehicle.vehicleCategoryId ?? WITHOUT_VALUE_KEY;
    const groupRows = groups.get(key);

    if (groupRows) {
      groupRows.push(row);
    } else {
      groups.set(key, [row]);
    }

    return groups;
  }, new Map());

  return Array.from(rowsByKey, ([key, groupRows]) => ({
    key,
    // An id with no codelist entry still names a real group, so it falls back to the raw id
    label:
      key === WITHOUT_VALUE_KEY
        ? i18n.t('general.labels.notSet')
        : (categoryLabels.get(key) ?? key),
    rows: groupRows,
  })).sort(byLabelWithoutValueLast);
}

const byLabelWithoutValueLast = (
  groupA: RentalCalendarRowGroup,
  groupB: RentalCalendarRowGroup
) => {
  if (groupA.key === WITHOUT_VALUE_KEY) {
    return 1;
  }

  if (groupB.key === WITHOUT_VALUE_KEY) {
    return -1;
  }

  return (groupA.label ?? '').localeCompare(groupB.label ?? '');
};

/** Collects the vehicles that have no category */
const WITHOUT_VALUE_KEY = 'withoutValue';
